import React, { useState, useEffect, useRef } from "react";
import { FieldGroup } from "../../../../components";

const WhatsappMessageWithHighlighting = ({
  messageText,
  productReference,
  onChange,
}) => {
  const handleChange = (valueOrEvent) => {
    let newValue;
    if (
      valueOrEvent &&
      typeof valueOrEvent === "object" &&
      valueOrEvent.target
    ) {
      newValue = valueOrEvent.target.value;
    } else {
      newValue = valueOrEvent;
    }

    const stringValue = newValue != null ? String(newValue) : "";
    onChange(stringValue);
  };
  const [highlightedMessage, setHighlightedMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const editableRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      try {
        if (messageText && productReference) {
          const highlighted = highlightReferenceNumbers(
            messageText,
            productReference
          );
          setHighlightedMessage(highlighted);
        } else {
          const messageStr = messageText != null ? String(messageText) : "";
          setHighlightedMessage(messageStr);
        }
      } catch (error) {
        console.error("Error in WhatsappMessageWithHighlighting:", error);
        const messageStr = messageText != null ? String(messageText) : "";
        setHighlightedMessage(messageStr);
      }
    }
  }, [messageText, productReference, isEditing]);

  useEffect(() => {
    if (!isEditing && editableRef.current) {
      editableRef.current.innerHTML = highlightedMessage;
    }
  }, [highlightedMessage, isEditing]);

  const highlightReferenceNumbers = (message, referenceNumber) => {
    const messageStr = message != null ? String(message) : "";
    const referenceStr = referenceNumber != null ? String(referenceNumber) : "";

    if (!referenceStr || !messageStr) {
      return messageStr;
    }

    // Match the alphanumeric prefix before the first separator (- / .
    // or space) rather than a blind substring(0, 6). The old logic
    // worked for refs like "126334-0027" (prefix "126334" — 6 chars,
    // no separator at position 6) but failed for Patek-style refs
    // like "5072R-001" or "4910/1200A-001" where the message in chat
    // shows just the prefix ("5072R", "4910") with no trailing
    // separator — substring(0,6)="5072R-" never matched "5072R\n".
    const prefixMatch = referenceStr.match(/^[A-Za-z0-9]+/);
    const prefix = prefixMatch ? prefixMatch[0] : referenceStr;
    if (prefix.length < 3) {
      return messageStr;
    }

    const normalizedPrefix = prefix.toLowerCase();
    const normalizedMessage = messageStr.toLowerCase();

    if (normalizedMessage.includes(normalizedPrefix)) {
      const regex = new RegExp(
        prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "gi"
      );

      const highlightedMessage = messageStr.replace(regex, (match) => {
        // No font-family / font-size here on purpose: an inline !important
        // cannot be overridden from CSS, so hardcoding 'Calibri Light' pinned
        // the highlighted reference to the legacy font inside an otherwise
        // RC-themed pop-up. Let it inherit the box's font instead.
        return `<span style="background-color: #79E08F !important; color: #0e0e0e !important; font-weight: bold !important; padding: 1px 4px; border-radius: 3px;">${match}</span>`;
      });

      return highlightedMessage;
    }

    return messageStr;
  };

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ 
        position: "absolute", 
        left: "-9999px", 
        top: "-9999px", 
        visibility: "hidden", 
        opacity: 0, 
        height: 0, 
        width: 0, 
        overflow: "hidden" 
      }}>
        <FieldGroup
          type="textarea"
          name="message_text"
          placeholder="Internal Comment [will not be displayed]"
          value={messageText != null ? String(messageText) : ""}
          onChange={handleChange}
          noFormik
        />
      </div>

      <div
        ref={editableRef}
        contentEditable
        style={{
          flex: "1",
          borderRadius: "5px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "12px",
          // Font family comes from the RC theme (.lm-modal-dark), not hardcoded
          // here — an inline value outranks the stylesheet and left this box on
          // the legacy font.
          fontSize: "14px",
          backgroundColor: "#212121",
          color: "#ffffff",
          whiteSpace: "pre-wrap",
          overflow: "auto",
          outline: "none",
          lineHeight: "1.4",
          minHeight: "80px",
        }}
        onInput={(e) => {
          const textContent = e.target.innerText || e.target.textContent || "";
          handleChange(textContent);
        }}
        onFocus={(e) => {
          setIsEditing(true);
          e.target.style.border = "1px solid #7145D0";
        }}
        onBlur={(e) => {
          setIsEditing(false);
          e.target.style.border = "1px solid rgba(255, 255, 255, 0.08)";
          const textContent = e.target.innerText || e.target.textContent || "";
          if (productReference && textContent) {
            const highlighted = highlightReferenceNumbers(
              textContent,
              productReference
            );
            if (highlighted !== textContent) {
              e.target.innerHTML = highlighted;
            }
          }
        }}
      />
    </div>
  );
};

export default WhatsappMessageWithHighlighting;
